import React from 'react';

import type {
  CodeBlockRedlineEditError,
  CodeBlockRedlineInsert,
  CodeBlockRedlineNote,
  CodeBlockRedlineRemove,
} from '@aline/common';
import { useReactiveVar } from '@apollo/client/react';
import { Skeleton } from '@mui/material';
import { useSelectedSuggestionUnit } from 'src/editor/EditorAlert/SuggestionsAlert/useNavigationHooks';
import { makeStyles } from 'tss-react/mui';

import { getFirstRedlineContext } from './getFirstRedlineContext';
import { redlineStatusesByMessageVar } from './redline-state-helpers';
import type { RedlineAction } from './redline-types';
import { RedlineActionItem } from './RedlineActionItem';
import { RedlineErrorItem } from './RedlineErrorItem';
import { RedlineInsertItem } from './RedlineInsertItem';
import { RedlineList } from './RedlineList';
import { RedlineNoteItem } from './RedlineNoteItem';
import { RedlineRemoveItem } from './RedlineRemoveItem';
import { useStableParsedRedlineItems } from './useStableParsedRedlineItems';
import { SuggestionStatus } from '../../../../chat-types';
import type { MarkdownComponentProps } from '../../../ChatPanelMarkdown';
import type { CodeBlockInfo } from '../../../remark-plugins/remarkCodeBlock';
import { getCodeBlockInfoFromNode } from '../../../remark-plugins/remarkCodeBlock';

type RenderableRow =
  | {
      action: RedlineAction;
      documentId: string;
      kind: 'action';
      locationAnchor: string;
    }
  | { error: CodeBlockRedlineEditError; kind: 'error' }
  | { insert: CodeBlockRedlineInsert; kind: 'insert' }
  | { kind: 'note'; note: CodeBlockRedlineNote }
  | { kind: 'remove'; remove: CodeBlockRedlineRemove };

const useStyles = makeStyles()(theme => ({
  skeleton: {
    borderRadius: 16,
  },
  skeletonContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 0, 0),
  },
}));

/**
 * Loading placeholder shown while redlines are being generated.
 */
export const GeneratingRedline = React.memo(function GeneratingRedline(): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.skeletonContainer}>
      <Skeleton className={classes.skeleton} height={200} variant="rectangular" width="100%" />
    </div>
  );
});

function getRowGroupId(row: RenderableRow): string {
  switch (row.kind) {
    case 'action':
      return row.action.groupId;
    case 'error':
      return row.error.groupId;
    case 'insert':
      return row.insert.groupId;
    case 'note':
      return row.note.groupId;
    case 'remove':
      return row.remove.groupId;
  }
}

/**
 * Renders a single renderable row as the item component matching its kind.
 */
const RedlineRowItem = React.memo(function RedlineRowItem({
  messageId,
  row,
  selectedGroupId,
  status,
  threadId,
}: Pick<CodeBlockInfo, 'messageId' | 'threadId'> & {
  row: RenderableRow;
  selectedGroupId: string | null;
  status: SuggestionStatus;
}): JSX.Element {
  switch (row.kind) {
    case 'error':
      return <RedlineErrorItem error={row.error} />;
    case 'insert':
      return (
        <RedlineInsertItem
          insert={row.insert}
          messageId={messageId}
          selectedGroupId={selectedGroupId}
          status={status}
          threadId={threadId}
        />
      );
    case 'note':
      return <RedlineNoteItem note={row.note} />;
    case 'remove':
      return (
        <RedlineRemoveItem
          messageId={messageId}
          remove={row.remove}
          selectedGroupId={selectedGroupId}
          status={status}
          threadId={threadId}
        />
      );
    case 'action':
      return (
        <RedlineActionItem
          actionWithContext={{
            action: row.action,
            documentId: row.documentId,
            locationAnchor: row.locationAnchor,
          }}
          messageId={messageId}
          selectedGroupId={selectedGroupId}
          status={status}
          threadId={threadId}
        />
      );
  }
});

/**
 * Renders the parsed redline edits as a list of action items.
 *
 * This component:
 * 1. Parses the JSONL code block into redline edits
 * 2. Reads computed statuses from the reactive variable (populated by ChatPanelMarkdown)
 * 3. Renders each action as a RedlineActionItem with its real status
 */
export const ParsedRedlineListElement = React.memo(function ParsedRedlineListElement({
  code,
  isNodeStreaming,
  messageId,
  threadId,
}: CodeBlockInfo): JSX.Element | null {
  const parsedItems = useStableParsedRedlineItems(code);
  /**
   * Flatten each parsed item into one or more renderable rows: edits become one
   * row per action (with parent context), inserts and errors each become a
   * single row.
   */
  const renderableRows = parsedItems.flatMap<RenderableRow>(item => {
    switch (item.type) {
      case 'redline-edit-error':
        return [{ error: item, kind: 'error' }];
      case 'redline-insert':
        return [{ insert: item, kind: 'insert' }];
      case 'redline-remove':
        return [{ kind: 'remove', remove: item }];
      case 'redline-note':
        return [{ kind: 'note', note: item }];
      case 'redline-edit':
        return item.actions.map(action => ({
          action,
          documentId: item.documentId,
          kind: 'action',
          locationAnchor: item.locationAnchor,
        }));
    }
  });

  // Document context for the header / "view in doc" button (first edit or insert).
  const firstRedlineContext = getFirstRedlineContext(parsedItems);
  const { selectedSuggestionUnit } = useSelectedSuggestionUnit();
  const selectedGroupId = selectedSuggestionUnit?.groupId ?? null;

  // Read computed statuses from reactive variable (populated by ChatPanelMarkdown useEffect)
  const redlineStatusesByMessage = useReactiveVar(redlineStatusesByMessageVar);
  const messageStatuses = redlineStatusesByMessage[messageId] ?? [];

  // Build a lookup from groupId to status for quick access
  const statusByGroupId = new Map<string, SuggestionStatus>();
  for (const { groupId, status } of messageStatuses) {
    statusByGroupId.set(groupId, status);
  }

  return (
    <RedlineList
      firstRedlineContext={firstRedlineContext}
      isLoading={isNodeStreaming}
      messageId={messageId}
      threadId={threadId}
    >
      {renderableRows.map(row => {
        const groupId = getRowGroupId(row);
        return (
          <RedlineRowItem
            key={groupId}
            messageId={messageId}
            row={row}
            selectedGroupId={selectedGroupId}
            status={statusByGroupId.get(groupId) ?? SuggestionStatus.Pending}
            threadId={threadId}
          />
        );
      })}
      {isNodeStreaming && <GeneratingRedline />}
    </RedlineList>
  );
});

/**
 * Entry point component for rendering redline edit code blocks.
 *
 * This component is registered as the renderer for code blocks with
 * the `jsonl-redline-edit` language identifier. It extracts the code
 * block metadata and delegates to ParsedRedlineListElement.
 *
 * Code block format (one JSONL line per attempted paragraph — successful
 * edits and per-paragraph failures are interleaved in emission order):
 * ```jsonl-redline-edit
 * {"type":"redline-edit","category":"in-paragraph","documentId":"...","locationAnchor":"...","actions":[...]}
 * {"type":"redline-edit-error","documentId":"...","locationAnchor":"...","groupId":"...","message":"..."}
 * ```
 */
export const CodeRedlineListElement = React.memo(function CodeRedlineListElement({
  node,
}: MarkdownComponentProps): JSX.Element | null {
  const codeBlockInfo = getCodeBlockInfoFromNode(node);

  if (!codeBlockInfo) return null;

  return <ParsedRedlineListElement {...codeBlockInfo} />;
});
